import {useEffect} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {socket} from '@/socket'
import {ConversationMessageDeletedEvent} from '../types'
import {applyConversationMessageDeletion} from '../utils/messagesCacheUtils'

/**
 * Subscribes to `conversation_message:deleted` on the shared socket and reconciles the React
 * Query cache live: the open conversation swaps the message for the "message deleted"
 * placeholder, and the conversations list plus the notification counters are refreshed.
 *
 * The backend emits to the `user:{userId}` room every member is joined to on connection, so
 * this hook only needs to add/remove the listener. It is mounted app-wide (see
 * NotificationsProvider) because list previews and unread badges must update even when the
 * conversation isn't open.
 */
export const useSocketConversationMessages = () => {
    const queryClient = useQueryClient()

    useEffect(() => {
        const handler = (raw: unknown) => {
            const parsed = ConversationMessageDeletedEvent.safeParse(raw)
            if (!parsed.success) {
                console.error('Invalid conversation_message:deleted payload received:', parsed.error)
                return
            }

            applyConversationMessageDeletion(queryClient, parsed.data.conversationId, parsed.data.messageId)
        }

        socket.on('conversation_message:deleted', handler)

        return () => {
            socket.off('conversation_message:deleted', handler)
        }
    }, [queryClient])
}
